import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { AppState, ALGORITHM_TYPE, ORDER_TYPE_LIST } from 'src/app';
import { ConfirmationOrder } from '@models/confirmation-order';
import { OperationService } from 'src/core';
import { of, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { selectCurrentContract } from 'src/app/state/selectors/current-contract.selectors';
import { flatMap, map, take } from 'rxjs/operators';
import { featureFlags } from 'src/app/featureFlags';
@Component({
  selector: 'app-confirmation-modal',
  templateUrl: './confirmation-modal.component.html',
  styleUrls: ['./confirmation-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ConfirmationModalComponent implements OnInit, OnDestroy {
  @Input() fractionalTitles = false;
  @Input() order: ConfirmationOrder;
  @Input() isOutOfTime?: boolean;
  @Output() onClose: EventEmitter<boolean> = new EventEmitter();
  @Output() confirmationHandler: EventEmitter<boolean> = new EventEmitter();

  public lockConfirmation: boolean;
  public lockCancel: boolean;
  public copyCallToAction: string;
  public amount: number;
  public price: number;
  public feeAmount: number;
  public ivaAmount: number;
  public approxTotal: number;

  private ivaRate: number;
  private currentCommision = 0;
  private noFeesTrading = featureFlags.noFeesTrading.isEnabled();
  private subscriptions: Subscription = new Subscription();

  constructor(
    private readonly operationService: OperationService,
    private readonly store: Store<AppState>,
  ) {}

  ngOnInit() {
    this.copyCallToAction = this.order.isBuy ? 'Comprar' : 'Vender';
    this.price = this.getPrice();
    this.amount = this.order.titles * this.price;

    if (this.amount > 0) {
      this.subscriptions.add(
        this.store
          .select(selectCurrentContract)
          .pipe(take(1))
          .subscribe(({ commissionBuy, commissionSell }) => {
            if (!this.noFeesTrading) {
              this.currentCommision = this.order.isBuy
                ? commissionBuy
                : commissionSell;
            }
            this.feeAmount = this.getFeeAmount();
          }),
      );

      this.subscriptions.add(
        this.store
          .select('params')
          .pipe(
            map((value: any) => value.toJS()),
            take(1),
            flatMap(({ IVA }) => {
              if (IVA === 0) {
                return this.operationService.getIVA();
              }
              return of(IVA);
            }),
          )
          .subscribe((iva) => {
            this.ivaRate = iva * this.currentCommision;
            this.ivaAmount = this.getIvaAmount();
            this.approxTotal = this.getApproxTotal();
          }),
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  cancelConfirmation() {
    this.lockConfirmation = true;
    this.lockCancel = false;

    this.onClose.next(true);
  }

  confirmation() {
    if (this.lockConfirmation) {
      return;
    }

    this.lockCancel = true;
    this.lockConfirmation = true;

    this.confirmationHandler.next(true);
  }

  getPrice() {
    let price = 0;

    switch (this.order.typeId) {
      default:
      case ORDER_TYPE_LIST.inLine:
        price = 0;
        break;
      case ORDER_TYPE_LIST.limit:
      case ORDER_TYPE_LIST.shortSell:
        price = this.order.price;
        break;
      case ORDER_TYPE_LIST.mplPasive:
      case ORDER_TYPE_LIST.mplActive:
      case ORDER_TYPE_LIST.mplHideVolume:
        price = this.order.triggerPrice;
        break;
      case ORDER_TYPE_LIST.stopInLine:
        price = this.order.stopPrice;
        break;
      case ORDER_TYPE_LIST.stopLimit:
        price = this.order.limitPrice;
        break;
      case ORDER_TYPE_LIST.oca:
      case ORDER_TYPE_LIST.ota:
        price = this.getPriceByAlgorithm(this.order);
        break;
    }

    return price;
  }

  getPriceByAlgorithm(order: ConfirmationOrder): number {
    let price = 0;

    switch (order.algorithmTypeId) {
      case ALGORITHM_TYPE.STOPMARKET:
        price = order.stopPrice;
        break;
      case ALGORITHM_TYPE.STOPLIMIT:
        price = order.limitPrice;
        break;
      default:
        price = order.price;
    }
    return price;
  }

  private getFeeAmount(): number {
    return this.amount * (this.currentCommision / 100);
  }

  private getIvaAmount(): number {
    return this.amount * (this.ivaRate / 100);
  }

  private getApproxTotal(): number {
    return this.order.isBuy
      ? this.amount + this.feeAmount + this.ivaAmount
      : this.amount - this.feeAmount - this.ivaAmount;
  }
}
